import React, { useEffect, useState } from "react";
import "./PaymentPage.css";

/**
 * Where the Cloud Functions live. Overridable only for local development; unset in every
 * production build, which therefore calls exactly what it always has.
 */
const FUNCTIONS_BASE_URL =
  process.env.REACT_APP_FUNCTIONS_BASE_URL
  || "https://us-central1-blocker-630c7.cloudfunctions.net";

const HANDOFF_TIMEOUT_MS = 2000;
const HANDOFF_RETRY_MS = 150;


const DESKTOP_DEVICE_TYPES = new Set([
  "windows", "macos",
]);

/** The link the desktop apps listen for. Unchanged for premium: no extra parameter. */
function deepLinkFor(status, purchase) {
  return purchase && purchase !== "premium"
    ? `blockp://payment?status=${purchase}`
    : `blockp://payment?status=${status}`;
}


function handoffToBlockP(status, purchase, setMessage, deviceType) {
  /*
   * A desktop app goes straight to its deep link.
   *
   * The announcement below exists to find out whether an extension is listening, because
   * the page otherwise cannot tell one caller from another. When the checkout said which
   * device it came from, there is nothing to find out: the extension did not open this,
   * and waiting two seconds for an answer that cannot come only delays the app - or, on a
   * machine where the browser happens to have the extension installed, hands the result
   * to the wrong one of the two.
   *
   * Only the device types listed above take this path. Anything unrecognised, and every
   * client that sends no device type at all, falls through to exactly what ran before.
   */
  if (DESKTOP_DEVICE_TYPES.has(String(deviceType || "").trim().toLowerCase())) {
    setMessage(
      status === "success"
        ? "Payment successful! Redirecting to the app..."
        : "Payment cancelled! Redirecting to the app..."
    );
    window.location.href = deepLinkFor(status, purchase);
    return;
  }

  let acknowledged = false;

  const onAck = (event) => {
    if (event.data?.type !== "BLOCKP_PAYMENT_RESULT_ACK") return;
    acknowledged = true;
    window.removeEventListener("message", onAck);
    window.clearInterval(retry);
    window.clearTimeout(fallback);
  };
  window.addEventListener("message", onAck);

  const announce = () =>
    window.postMessage({ type: "BLOCKP_PAYMENT_RESULT", status, purchase }, window.location.origin);

  announce();
  const retry = window.setInterval(announce, HANDOFF_RETRY_MS);

  const fallback = window.setTimeout(() => {
    window.clearInterval(retry);
    window.removeEventListener("message", onAck);
    if (acknowledged) return;
    setMessage(
      status === "success"
        ? "Payment successful! Redirecting to the app..."
        : "Payment cancelled! Redirecting to the app..."
    );
    window.location.href = deepLinkFor(status, purchase);
  }, HANDOFF_TIMEOUT_MS);
}

const PaymentPage = () => {
  const [status, setStatus] = useState("loading");
  const [message, setMessage] = useState("");

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const result = params.get("result");

    if (result === "success" || result === "cancel") {
      setStatus(result === "success" ? "success" : "cancelled");
      setMessage(
        result === "success"
          ? "Payment successful! Returning to BlockP..."
          : "Payment cancelled! Returning to BlockP..."
      );
      handoffToBlockP(result, params.get("purchase") || "premium", setMessage, params.get("deviceType"));
      return;
    }

    // No result param — initiate checkout
    const uid = params.get("uid");
    const subscriptionType = params.get("subscriptionType");
    const email = params.get("email");
    const deviceType = params.get("deviceType");
    // Stripe test mode, requested by whichever client opened this page. Absent means real
    // money, which is the only safe default.
    const isTestMode = params.get("mode") === "test";

    if (!uid || !subscriptionType) {
      setStatus("error");
      setMessage("Invalid request: missing uid or subscriptionType.");
      return;
    }
    redirectToCheckout(uid, subscriptionType, email, deviceType, isTestMode);
  }, []);

  const redirectToCheckout = async (uid, subscriptionType, email, deviceType, isTestMode) => {
    try {
      const endpoint = isTestMode ? "createCheckoutSessionSandbox" : "createCheckoutSession";
      const res = await fetch(
        `${FUNCTIONS_BASE_URL}/${endpoint}`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ uid, subscriptionType, email, deviceType }),
        }
      );

      const data = await res.json();

      if (!res.ok || !data.url) {
        throw new Error(data.error || "Failed to create checkout session.");
      }
      window.location.href = data.url;
    } catch (err) {
      setStatus("error");
      setMessage(err.message);
    }
  };

  return (
    <div className="payment-page-container">
      {status === "loading" && (
        <div className="loading-container">
          <div className="spinner" />
          <p>Redirecting to secure payment…</p>
        </div>
      )}

      {status === "success" && (
        <div className="success-container">
          <h2>Payment Successful!</h2>
          <p>{message}</p>
        </div>
      )}

      {status === "cancelled" && (
        <div className="cancelled-container">
          <h2>Payment Cancelled</h2>
          <p>{message}</p>
        </div>
      )}

      {status === "error" && (
        <div className="error-container">
          <h2>Something went wrong</h2>
          <p>{message}</p>
        </div>
      )}
    </div>
  );
};

export default PaymentPage;
